import { PageTitle } from '@/shared/components/common/PageTitle'
import { Badge } from '@/shared/components/ui/badge'
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card'
import { DatePicker } from '@/shared/components/ui/date-picker'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Switch } from '@/shared/components/ui/switch'
import type { Contract } from '../../services/contractService'
import type { Customer } from '../../services/customerService'
import type { Plan } from '../../services/plansService'

interface SubscriptionDetailsProps {
  availablePlans: Plan[]
  selectedPlanId: string
  setSelectedPlanId: (planId: string) => void
  selectedPlan: Plan | null
  currency: string
  setCurrency: (currency: string) => void
  startDate: Date | undefined
  setStartDate: (date: Date | undefined) => void
  endDate: Date | undefined
  setEndDate: (date: Date | undefined) => void
  isForever: boolean
  setIsForever: (forever: boolean) => void
  // Optional props for contract view mode
  contract?: Contract
  customer?: Customer
  // For making core fields read-only in contract view
  readonly?: boolean
}

export function SubscriptionDetails({
  availablePlans,
  selectedPlanId,
  setSelectedPlanId,
  selectedPlan,
  currency,
  setCurrency,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  isForever,
  setIsForever,
  contract,
  customer,
  readonly = false,
}: SubscriptionDetailsProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'default'
      case 'draft':
        return 'secondary'
      case 'suspended':
        return 'secondary'
      case 'canceled':
        return 'destructive'
      case 'expired':
        return 'outline'
      default:
        return 'outline'
    }
  }
  return (
    <>
      <div className='mb-8 flex items-center justify-between'>
        <PageTitle
          title={contract?.name || customer?.name || 'Customer'}
          subtitle={'Contract'}
        />
        <Badge
          variant={getStatusColor(contract?.status || 'active')}
          className='text-transform: capitalize'
        >
          {contract?.status}
        </Badge>
      </div>

      <Card className='bg-transparent border'>
        <CardHeader></CardHeader>
        <CardContent className='space-y-6'>
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
            <div className='space-y-2'>
              <Label htmlFor='plan-select'>Plan *</Label>
              <Select
                value={selectedPlanId}
                onValueChange={setSelectedPlanId}
                disabled={readonly}
              >
                <SelectTrigger id='plan-select'>
                  <SelectValue placeholder='Select a plan' />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(availablePlans) ? (
                    availablePlans.map(plan => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value='' disabled>
                      No plans available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='billing-period'>Billing Period *</Label>
              <Select
                value={selectedPlan?.billing_interval || 'monthly'}
                onValueChange={_value => {
                  // Billing period is read-only, determined by plan
                }}
                disabled={readonly || !!selectedPlan} // Make read-only when plan is selected or in readonly mode
              >
                <SelectTrigger id='billing-period'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='monthly'>Monthly</SelectItem>
                  <SelectItem value='yearly'>Yearly</SelectItem>
                  <SelectItem value='quarterly'>Quarterly</SelectItem>
                </SelectContent>
              </Select>
              {selectedPlan && (
                <p className='text-xs text-muted-foreground'>
                  Billing period is determined by the selected plan
                </p>
              )}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='currency-select'>Currency *</Label>
              <Select
                value={currency}
                onValueChange={setCurrency}
                disabled={readonly}
              >
                <SelectTrigger id='currency-select'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='USD'>USD</SelectItem>
                  <SelectItem value='EUR'>EUR</SelectItem>
                  <SelectItem value='GBP'>GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            <div className='space-y-2'>
              <Label htmlFor='start-date'>Start Date *</Label>
              <DatePicker
                date={startDate}
                onDateChange={setStartDate}
                placeholder='Select start date'
                disabled={readonly}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='end-date'>End Date</Label>
              <div className='flex items-center gap-2'>
                <div className='flex-1'>
                  <DatePicker
                    date={endDate}
                    onDateChange={setEndDate}
                    placeholder={isForever ? 'Forever' : 'Select end date'}
                    disabled={isForever || readonly}
                  />
                </div>
                <div className='flex items-center space-x-2'>
                  <Switch
                    id='forever-toggle'
                    checked={isForever}
                    onCheckedChange={checked => {
                      setIsForever(checked)
                      if (checked) {
                        setEndDate(undefined)
                      }
                    }}
                    disabled={readonly}
                  />
                  <Label
                    htmlFor='forever-toggle'
                    className='text-sm whitespace-nowrap'
                  >
                    Forever
                  </Label>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  )
}
